"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, ArrowRight, BarChart3, AlertCircle, FileSearch } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
export default function AuditMeasurement() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const auditItems = ["Brand appearances in sampled AI answers across ChatGPT, Google AI Overviews / AI Mode, Perplexity, Copilot and any niche tool relevant to your sector", "Commercially relevant prompts that mirror how real buyers research solutions", "Competitor visibility analysis showing which brands appear and which sources support them", "Content and topical gap assessment across core service and decision pages", "Technical accessibility checks for crawler access, rendering, and robots directives", "Brand and entity consistency across your site, directories, and third-party profiles", "Existing referral traffic and qualified enquiries attributable to AI where data exists"];
  const measurementSignals = ["Referral traffic from identifiable AI sources where standard referrers or campaign UTM parameters exist", "Qualified enquiries, inbound telephone calls, and consultation bookings", "Traditional organic search metrics (impressions, clicks, rankings) acting as the underlying baseline", "Sampled AI answer mentions, captured with prompt logs and screenshots over time", "Competitor visibility snapshots demonstrating relative movement across target prompts", "Completed technical, structured data, and content clarity improvements"];
  return <section id="aiauditmeasurement" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-14 md:mb-20">
          <Badge variant="secondary" className="px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase mb-4 inline-flex items-center gap-1.5">
            <BarChart3 className="size-3.5 text-accent" />
            <span>Auditing & Tracking</span>
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight text-foreground mb-5">
            AI Search Visibility Audit and Measurement
          </h2>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Effective AI search optimisation relies on methodical auditing and transparent, realistic tracking. I evaluate where your brand currently appears in generative answers and establish an honest framework to measure progress over time.
          </p>
        </div>

        {/* Two-Column Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch mb-16">
          {/* Column 1: What the audit covers */}
          <motion.div variants={cardVariants} className="flex flex-col h-full">
            <Card className="flex flex-col justify-between h-full rounded-xl border border-border shadow-sm hover:border-primary/40 bg-card transition-[border-color,box-shadow] duration-200">
              <CardHeader className="pt-6 pb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 text-primary">
                    <FileSearch className="size-6" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-accent font-sans">
                    Scope & Analysis
                  </span>
                </div>

                <CardTitle className="text-xl md:text-2xl font-serif font-semibold text-card-foreground">
                  What my AI visibility audit covers
                </CardTitle>

                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  This is delivered as a standalone engagement, similar in structure to my comprehensive{" "}
                  <Link to="/SeoAuditsPage" className="text-primary font-medium underline underline-offset-4 hover:text-primary/80 transition-colors">
                    SEO audits
                  </Link>
                  , ending in a prioritised action plan for your team or developers.
                </p>
              </CardHeader>

              <CardContent className="flex-1 py-2 flex flex-col justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 font-sans">
                    Audit deliverables and evaluation areas:
                  </div>
                  <ul className="space-y-3 mb-6">
                    {auditItems.map((item, index) => <li key={index} className="flex items-start gap-2.5 text-sm text-muted-foreground leading-relaxed">
                        <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Check className="size-2.5 stroke-[3]" />
                        </span>
                        <span>{item}</span>
                      </li>)}
                  </ul>
                </div>

                <div className="p-4 rounded-lg bg-muted/60 border border-border/80 text-xs text-muted-foreground leading-relaxed">
                  <strong className="text-foreground font-medium">Outcome: </strong>
                  A clear, prioritised roadmap identifying which content, technical barriers, and authority gaps need attention first.
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Column 2: How progress is measured and the limits */}
          <motion.div variants={cardVariants} className="flex flex-col h-full">
            <Card className="flex flex-col justify-between h-full rounded-xl border border-border shadow-sm hover:border-primary/40 bg-card transition-[border-color,box-shadow] duration-200">
              <CardHeader className="pt-6 pb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 text-primary">
                    <BarChart3 className="size-6" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-accent font-sans">
                    Tracking & Reality
                  </span>
                </div>

                <CardTitle className="text-xl md:text-2xl font-serif font-semibold text-card-foreground">
                  How I measure progress, and the limits
                </CardTitle>

                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  I report on practical signals and business outcomes while being direct about what AI search analytics can and cannot show.
                </p>
              </CardHeader>

              <CardContent className="flex-1 py-2 flex flex-col justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 font-sans">
                    Signals I monitor over time:
                  </div>
                  <ul className="space-y-3 mb-6">
                    {measurementSignals.map((item, index) => <li key={index} className="flex items-start gap-2.5 text-sm text-muted-foreground leading-relaxed">
                        <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Check className="size-2.5 stroke-[3]" />
                        </span>
                        <span>{item}</span>
                      </li>)}
                  </ul>
                </div>

                <div className="p-4 rounded-lg bg-muted/60 border border-border/80 text-xs text-muted-foreground leading-relaxed flex items-start gap-2.5">
                  <AlertCircle className="size-4 text-primary shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-foreground font-medium">Tracking limits: </strong>
                    AI tools often send no standard referral data, and outputs vary between sessions. I use repeated sampling and trend observation rather than single screenshots. Findings are directional intelligence, not precise analytics.
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        {/* Bottom CTA Row */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-8 py-3 text-base shadow-sm font-medium">
            <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
              <span>Book a Free SEO Consultation</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>

          <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border text-foreground hover:bg-accent hover:text-accent-foreground rounded-lg px-8 py-3 text-base font-medium">
            <Link to="/SeoAuditsPage">
              <span>Get a Free SEO Audit</span>
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}